'use client';

import { useState, useEffect } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import ColumnSelectorModal from './Column-Select';

import CompactTableHeader from './Table-Header';

import SmartPagination from '../../../utils/Smart-Pagination';
import CompactSidebarFilter from '../../../utils/Compact-Sidebar-Filter';
import AddBtn from '../../../components/common/AddBtn';
import QuickAddBtn from '../../../components/common/QuickAddBtn';

import { Link } from 'react-router-dom';

const PAGE_SINGLE_TITLE = 'Activity';
const LINK_URL = 'activitys';

// Define available columns
const AVAILABLE_COLUMNS = [
  { key: 'serial', label: 'Serial #', icon: 'fa-hashtag', required: true },
  { key: 'task_name', label: 'Task Name', icon: 'fa-tasks', required: true },
  { key: 'assigned_to', label: 'Assigned To', icon: 'fa-user' },
  { key: 'due_time', label: 'Due Time', icon: 'fa-calendar' },
  { key: 'estimated_time', label: 'Estimated Time', icon: 'fa-clock' },
  { key: 'actual_time', label: 'Actual Time', icon: 'fa-check-circle' },
  { key: 'client', label: 'Client', icon: 'fa-building' },
  { key: 'process', label: 'Process', icon: 'fa-cogs' },
  { key: 'activity_cycle', label: 'Cycle', icon: 'fa-recycle' },
  { key: 'activity_status', label: 'Activity Status', icon: 'fa-flag' },
  { key: 'review_status', label: 'Review Status', icon: 'fa-eye' },
  { key: 'actions', label: 'Actions', icon: 'fa-ellipsis-h', required: true },
];

// Default visible columns
const DEFAULT_VISIBLE_COLUMNS = {
  serial: true,
  task_name: true,
  assigned_to: true,
  due_time: true,
  estimated_time: true,
  actual_time: true,
  client: true,
  process: true,
  activity_cycle: false,
  activity_status: true,
  review_status: true,
  actions: true,
};

const tableStyles = {
  tableContainer: {
    position: 'relative',
    overflow: 'auto',
    maxHeight: '75vh',
  },
  table: {
    tableLayout: 'fixed',
    minWidth: '1400px',
  },
  cellConstraints: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stickyActions: {
    position: 'sticky',
    right: 0,
    backgroundColor: '#ffffff',
    borderLeft: '2px solid #dee2e6',
    boxShadow: '-3px 0 6px rgba(0,0,0,0.08)',
    zIndex: 10,
  },
  actionButton: {
    fontSize: '12px',
    padding: '6px 10px',
    minWidth: '34px',
    height: '32px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
  },
  actionIcon: {
    fontSize: '13px',
  },
};

const EditModal = ({
  show,
  onHide,
  editData,
  dropdownOptions,
  onSave,
  loading,
}) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (editData?.currentValue) {
      setValue(editData.currentValue);
    }
  }, [editData]);

  const handleSave = () => {
    const currentSelection = dropdownOptions?.[editData?.field]?.find(
      (o) => o.value === value
    );

    if (
      editData?.field === 'activity_status' &&
      currentSelection.label === 'COMPLETED'
    ) {
      const isCheckedAll = editData?.single?.checklist?.every(
        (item) => item.is_checked === true
      );

      if (!isCheckedAll) {
        toast.error(
          'Please check all checklist items before marking as completed.'
        );
        return;
      }
    }

    onSave(editData?.activityId, editData?.field, value);
  };

  if (!show) return null;

  return (
    <div
      className='modal fade show d-block'
      tabIndex='-1'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              <i className='fa fa-edit me-2'></i>
              Edit {editData?.fieldLabel}
            </h5>
            <button
              type='button'
              className='btn-close'
              onClick={onHide}
            ></button>
          </div>
          <div className='modal-body'>
            <div className='mb-3'>
              <label className='form-label fw-semibold'>
                {editData?.fieldLabel}
              </label>
              {editData?.field === 'assigned_to' ||
              editData?.field === 'client' ||
              editData?.field === 'process' ||
              editData?.field === 'activity_cycle' ||
              editData?.field === 'activity_status' ||
              editData?.field === 'review_status' ? (
                <select
                  className='form-select'
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                >
                  <option value=''>Select {editData?.fieldLabel}</option>
                  {dropdownOptions?.[editData?.field]?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : editData?.field === 'due_time' ? (
                <input
                  type='datetime-local'
                  className='form-control'
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              ) : editData?.field === 'estimated_time' ||
                editData?.field === 'actual_time' ? (
                <input
                  type='number'
                  className='form-control'
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder='Enter time in hours'
                />
              ) : (
                <input
                  type='text'
                  className='form-control'
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={`Enter ${editData?.fieldLabel.toLowerCase()}`}
                />
              )}
            </div>
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={onHide}
            >
              <i className='fa fa-times me-1'></i>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div
                    className='spinner-border spinner-border-sm me-2'
                    role='status'
                  ></div>
                  Saving...
                </>
              ) : (
                <>
                  <i className='fa fa-save me-1'></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirmation modal for delete action
const ConfirmDeleteModal = ({ show, onHide, onConfirm }) => {
  if (!show) return null;
  return (
    <div
      className='modal fade show d-block'
      tabIndex='-1'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              <i className='fa fa-exclamation-triangle text-danger me-2'></i>
              Confirm Delete
            </h5>
            <button
              type='button'
              className='btn-close'
              onClick={onHide}
            ></button>
          </div>
          <div className='modal-body'>
            <p>
              Are you sure you want to delete this item? This action cannot be
              undone.
            </p>
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={onHide}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-danger'
              onClick={onConfirm}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EnhancedTableSection = ({
  SIDEBAR_OPTIONS,
  dropdownOptions,
  total_estimated_time,
  total_actual_time,
  total_activitys,
  activitys_loading,
  activitys,
  page,
  pages,
  setPageNumber,
  LIMIT,
  updateStatus,
  deleteBtnClicked,
  setModal,
  setModalData,
  setActivity,
  handleChecklist,
  loadingActivityStatus,
  loadOptions,

  statusCounts,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);

  // New state for sidebar toggle
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Load saved column preferences from localStorage
  useEffect(() => {
    const savedColumns = localStorage.getItem('activity-table-columns');
    if (savedColumns) {
      try {
        setVisibleColumns(JSON.parse(savedColumns));
      } catch (error) {
        console.error('Error loading saved columns:', error);
      }
    }

    // Load saved sidebar state
    const savedSidebarState = localStorage.getItem('sidebar-collapsed');
    if (savedSidebarState) {
      setSidebarCollapsed(JSON.parse(savedSidebarState));
    }
  }, []);

  // Save sidebar state to localStorage
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      COMPLETED: 'badge bg-success',
      'IN PROGRESS': 'badge bg-primary',
      TODO: 'badge bg-secondary',
      'ON-HOLD': 'badge bg-warning text-dark',
      PENDING: 'badge bg-warning text-dark',
      APPROVED: 'badge bg-success',
      REJECTED: 'badge bg-danger',
    };
    return statusClasses[status] || 'badge bg-light text-dark';
  };

  const handleEdit = (activityId, field, currentValue, fieldLabel, single) => {
    setEditData({ activityId, field, currentValue, fieldLabel, single });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (activityId, field, value) => {
    const newValues = { [field]: value };
    await updateStatus(activityId, newValues);
    setShowEditModal(false);
    setEditData(null);
  };

  const handleSaveColumns = (newVisibleColumns) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem(
      'activity-table-columns',
      JSON.stringify(newVisibleColumns)
    );
    toast.success('Column preferences saved!');
  };

  const getVisibleColumnsCount = () => {
    return Object.values(visibleColumns).filter(Boolean).length;
  };

  // Replace delete button handler
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteBtnClicked(deleteId);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  return (
    <div className='container-fluid'>
      <div className='row'>
        {/* Collapsible Sidebar */}
        {SIDEBAR_OPTIONS && (
          <div
            className={`${
              sidebarCollapsed ? 'd-none' : 'col-xl-2 col-lg-3'
            } transition-all`}
            style={{
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <CompactSidebarFilter
              SIDEBAR_OPTIONS={SIDEBAR_OPTIONS}
              dropdown_options={dropdownOptions}
              loadOptions={loadOptions}
              // onToggle={toggleSidebar}
              isCollapsed={sidebarCollapsed}
            />
          </div>
        )}

        {/* Main Table Section */}
        <div
          className={`${
            sidebarCollapsed ? 'col-lg-12' : 'col-xl-10 col-lg-9'
          } transition-all`}
        >
          <div className=' shadow-sm border-0'>
            {/* Table Header with Sidebar Toggle */}
            <div
              className='card-header bg-white border-bottom '
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center'>
                  {/* Sidebar Toggle Button */}
                  <button
                    className='btn btn-outline-secondary btn-sm me-3'
                    onClick={toggleSidebar}
                    title={sidebarCollapsed ? 'Show Filters' : 'Hide Filters'}
                    style={{
                      fontSize: '12px',
                      padding: '6px 10px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <i
                      className={`fa ${
                        sidebarCollapsed ? 'fa-filter' : 'fa-times'
                      } me-1`}
                      style={{ fontSize: '11px' }}
                    ></i>
                    {sidebarCollapsed ? 'Show Filters' : 'Hide Filters'}
                  </button>
                  <h5 className='mb-0 text-dark'>Activity Management</h5>
                </div>
              </div>

              <div className='modern-action-bar'>
                <div className='modern-action-left'>
                  <div className='modern-button-group'>
                    <AddBtn item={LINK_URL} title={PAGE_SINGLE_TITLE} />
                    <QuickAddBtn title={'Quick Activity'} />
                  </div>
                </div>
                <div className='modern-action-right'>
                  <div className='modern-button-group'>
                    <Link
                      className='modern-btn modern-btn-danger'
                      to={`/activitys?conditional[due_time][$lt]=${moment().format(
                        'YYYY-MM-DD'
                      )}&exact[activity_status]=62b2a7abdb1f21d46398f0b8`}
                    >
                      <i className='fas fa-exclamation-triangle modern-btn-icon'></i>
                      Overdue Tasks
                    </Link>
                    <Link
                      className='modern-btn modern-btn-info'
                      to={'/activitys?exact[process]=62d7b431b8d5fd9c17406a60'}
                    >
                      <i className='fab fa-google modern-btn-icon'></i>
                      Google Ads Activity
                    </Link>
                    <Link
                      className='modern-btn modern-btn-primary'
                      to={'/activitys?exact[process]=62d7b495b8d5fd9c17406a61'}
                    >
                      <i className='fab fa-facebook modern-btn-icon'></i>
                      Facebook Ads Activity
                    </Link>
                    <Link
                      to={'/activitys/multiple-add'}
                      className='modern-btn modern-btn-secondary'
                    >
                      <i className='fas fa-upload modern-btn-icon'></i>
                      Multi Add
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Move CompactTableHeader outside and make it a separate section */}
            <CompactTableHeader
              total_estimated_time={total_estimated_time}
              total_actual_time={total_actual_time}
              total_activitys={total_activitys}
              onQuickAdd={() => setShowQuickAdd(true)}
              statusCounts={statusCounts || {}}
            />

            <div className='card-body pt-0'>
              {/* Records Count & Column Selector */}
              <div className='d-flex justify-content-between align-items-center mb-3 mt-3'>
                <div
                  className='text-muted'
                  style={{ fontSize: '12px', fontWeight: '500' }}
                >
                  <i
                    className='fa fa-list me-2'
                    style={{ fontSize: '11px' }}
                  ></i>
                  {total_activitys} records found
                  {sidebarCollapsed && (
                    <span
                      className='badge bg-info ms-2'
                      style={{ fontSize: '10px' }}
                    >
                      <i className='fa fa-expand-arrows-alt me-1'></i>
                      Full Width
                    </span>
                  )}
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <span
                    className='badge bg-light text-dark'
                    style={{ fontSize: '10px' }}
                  >
                    {getVisibleColumnsCount()} columns visible
                  </span>
                  <button
                    className='btn btn-outline-secondary btn-sm'
                    onClick={() => setShowColumnSelector(true)}
                    title='Customize Columns'
                  >
                    <i
                      className='fa fa-columns me-1'
                      style={{ fontSize: '10px' }}
                    ></i>
                    Columns
                  </button>
                </div>
              </div>

              {/* Dynamic Table */}
              <div
                className='table-responsive'
                style={{
                  position: 'relative',
                  overflow: 'auto',
                  maxHeight: '75vh',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                }}
              >
                <table
                  className='table table-hover align-middle'
                  style={{
                    ...tableStyles.table,
                    border: '1px solid #e9ecef',
                  }}
                >
                  {!activitys_loading && (
                    <thead>
                      <tr
                        className='table-light'
                        style={{ borderBottom: '2px solid #dee2e6' }}
                      >
                        {visibleColumns.serial && (
                          <th
                            className='border-end py-3 text-center'
                            style={{
                              width: '70px',
                              maxWidth: '70px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>#</span>
                          </th>
                        )}
                        {visibleColumns.task_name && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '180px',
                              maxWidth: '280px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>TASK NAME</span>
                          </th>
                        )}
                        {visibleColumns.assigned_to && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '140px',
                              maxWidth: '200px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>ASSIGNED TO</span>
                          </th>
                        )}
                        {visibleColumns.due_time && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '170px',
                              maxWidth: '170px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>DUE TIME</span>
                          </th>
                        )}
                        {visibleColumns.estimated_time && (
                          <th
                            className='border-end py-3 text-center'
                            style={{
                              width: '130px',
                              maxWidth: '130px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>EST. TIME</span>
                          </th>
                        )}
                        {visibleColumns.actual_time && (
                          <th
                            className='border-end py-3 text-center'
                            style={{
                              width: '130px',
                              maxWidth: '130px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>ACT. TIME</span>
                          </th>
                        )}
                        {visibleColumns.client && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '160px',
                              maxWidth: '160px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>CLIENT</span>
                          </th>
                        )}
                        {visibleColumns.process && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '160px',
                              maxWidth: '160px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>PROCESS</span>
                          </th>
                        )}
                        {visibleColumns.activity_cycle && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '140px',
                              maxWidth: '140px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>CYCLE</span>
                          </th>
                        )}
                        {visibleColumns.activity_status && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '150px',
                              maxWidth: '150px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>STATUS</span>
                          </th>
                        )}
                        {visibleColumns.review_status && (
                          <th
                            className='border-end py-3'
                            style={{
                              width: '150px',
                              maxWidth: '150px',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRight: '1px solid #e9ecef',
                            }}
                          >
                            <span className='text-muted'>REVIEW</span>
                          </th>
                        )}
                        {visibleColumns.actions && (
                          <th
                            className='py-3 text-center'
                            style={{
                              position: 'sticky',
                              right: 0,
                              backgroundColor: '#f8f9fa',
                              borderLeft: '1px solid #dee2e6',
                              boxShadow: '-2px 0 4px rgba(0,0,0,0.05)',
                              zIndex: 5,
                              width: '80px',
                              minWidth: '80px',
                              fontSize: '11px',
                              fontWeight: '600',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span className='text-muted'>ACTIONS</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                  )}

                  <tbody>
                    {!activitys_loading ? (
                      activitys &&
                      activitys.map((single, index) => {
                        const isOverdue =
                          single.activity_status &&
                          single.activity_status.name !== 'COMPLETED' &&
                          single.activity_status.name !== 'ON-HOLD' &&
                          moment().diff(
                            moment(single.due_time).utc(),
                            'hours'
                          ) > 0;

                        return (
                          <tr
                            key={single._id}
                            className={
                              isOverdue
                                ? 'table-danger table-danger-subtle'
                                : ''
                            }
                            style={{ borderBottom: '1px solid #f8f9fa' }}
                          >
                            {/* Serial Number */}
                            {visibleColumns.serial && (
                              <td
                                className='border-end py-3 text-center'
                                style={{
                                  borderRight: '1px solid #f8f9fa',
                                  width: '70px',
                                  maxWidth: '70px',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <span
                                  className='badge bg-light text-dark'
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                  }}
                                >
                                  {index + 1 + (page - 1) * LIMIT}
                                </span>
                              </td>
                            )}

                            {/* Task Name */}
                            {visibleColumns.task_name && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '280px',
                                  maxWidth: '280px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <div
                                    className='text-truncate me-2'
                                    title={single.task_name}
                                    style={{ maxWidth: '180px' }}
                                  >
                                    <span
                                      style={{
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        color: '#212529',
                                      }}
                                    >
                                      {single.task_name}
                                    </span>
                                  </div>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'task_name',
                                        single.task_name,
                                        'Task Name'
                                      )
                                    }
                                    title='Edit Task Name'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Assigned To */}
                            {visibleColumns.assigned_to && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '200px',
                                  maxWidth: '200px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <div className='d-flex align-items-center'>
                                    <div className='text-truncate'>
                                      <span
                                        style={{
                                          fontSize: '13px',
                                          fontWeight: '500',
                                          color: '#495057',
                                        }}
                                      >
                                        {single.assigned_to?.name}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'assigned_to',
                                        single.assigned_to?._id,
                                        'Assigned To'
                                      )
                                    }
                                    title='Edit Assigned User'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Due Time */}
                            {visibleColumns.due_time && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '170px',
                                  maxWidth: '170px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <div>
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        color: '#212529',
                                      }}
                                    >
                                      {single.due_time &&
                                        moment(single.due_time)
                                          .utc()
                                          .format('DD-MM-YYYY')}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        color: '#6c757d',
                                      }}
                                    >
                                      {single.due_time &&
                                        moment(single.due_time)
                                          .utc()
                                          .format('hh:mm A')}
                                    </div>
                                    {isOverdue && (
                                      <div className='mt-1'>
                                        <span
                                          className='badge bg-danger'
                                          style={{ fontSize: '10px' }}
                                        >
                                          <i
                                            className='fa fa-exclamation-triangle me-1'
                                            style={{ fontSize: '9px' }}
                                          ></i>
                                          Overdue
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'due_time',
                                        moment(single.due_time).format(
                                          'YYYY-MM-DDTHH:mm'
                                        ),
                                        'Due Time'
                                      )
                                    }
                                    title='Edit Due Time'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Estimated Time */}
                            {visibleColumns.estimated_time && (
                              <td
                                className='border-end py-3 text-center'
                                style={{
                                  width: '130px',
                                  maxWidth: '130px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-center'>
                                  <span
                                    className='badge bg-light text-dark me-2'
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {single.estimated_time}min
                                  </span>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'estimated_time',
                                        single.estimated_time,
                                        'Estimated Time'
                                      )
                                    }
                                    title='Edit Estimated Time'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Actual Time */}
                            {visibleColumns.actual_time && (
                              <td
                                className='border-end py-3 text-center'
                                style={{
                                  width: '130px',
                                  maxWidth: '130px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-center'>
                                  <span
                                    className='badge bg-light text-dark me-2'
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {single.actual_time}min
                                  </span>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'actual_time',
                                        single.actual_time,
                                        'Actual Time'
                                      )
                                    }
                                    title='Edit Actual Time'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Client */}
                            {visibleColumns.client && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '160px',
                                  maxWidth: '160px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <div
                                    className='text-truncate me-2'
                                    title={single.client?.name}
                                  >
                                    <span
                                      style={{
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        color: '#495057',
                                      }}
                                    >
                                      {single.client?.name}
                                    </span>
                                  </div>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'client',
                                        single.client?._id,
                                        'Client'
                                      )
                                    }
                                    title='Edit Client'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Process */}
                            {visibleColumns.process && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '160px',
                                  maxWidth: '160px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <div
                                    className='text-truncate me-2'
                                    title={single.process?.name}
                                  >
                                    <span
                                      style={{
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        color: '#495057',
                                      }}
                                    >
                                      {single.process?.name}
                                    </span>
                                  </div>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'process',
                                        single.process?._id,
                                        'Process'
                                      )
                                    }
                                    title='Edit Process'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Activity Cycle */}
                            {visibleColumns.activity_cycle && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '140px',
                                  maxWidth: '140px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <span
                                    className='badge bg-light text-dark'
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {single.activity_cycle?.name}
                                  </span>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'activity_cycle',
                                        single.activity_cycle?._id,
                                        'Activity Cycle'
                                      )
                                    }
                                    title='Edit Activity Cycle'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Activity Status */}
                            {visibleColumns.activity_status && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '150px',
                                  maxWidth: '150px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <span
                                    className={getStatusBadge(
                                      single.activity_status?.name
                                    )}
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {single.activity_status?.name}
                                  </span>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'activity_status',
                                        single.activity_status?._id,
                                        'Activity Status',
                                        single
                                      )
                                    }
                                    title='Edit Activity Status'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Review Status */}
                            {visibleColumns.review_status && (
                              <td
                                className='border-end py-3'
                                style={{
                                  width: '150px',
                                  maxWidth: '150px',
                                  borderRight: '1px solid #f8f9fa',
                                  ...tableStyles.cellConstraints,
                                }}
                              >
                                <div className='d-flex align-items-center justify-content-between'>
                                  <span
                                    className={getStatusBadge(
                                      single.review_status?.name
                                    )}
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {single.review_status?.name}
                                  </span>
                                  <button
                                    className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                    onClick={() =>
                                      handleEdit(
                                        single._id,
                                        'review_status',
                                        single.review_status?._id,
                                        'Review Status'
                                      )
                                    }
                                    title='Edit Review Status'
                                    style={{ fontSize: '11px' }}
                                  >
                                    <i className='fa fa-edit'></i>
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Actions */}
                            {visibleColumns.actions && (
                              <td
                                className='py-3 text-center'
                                style={{
                                  position: 'sticky',
                                  right: 0,
                                  backgroundColor: '#ffffff',
                                  borderLeft: '1px solid #dee2e6',
                                  boxShadow: '-2px 0 4px rgba(0,0,0,0.05)',
                                  zIndex: 5,
                                  width: '40px',
                                  minWidth: '40px',
                                }}
                              >
                                <div className='d-flex justify-content-center align-items-center gap-1'>
                                  <button
                                    className='btn btn-outline-primary btn-sm'
                                    onClick={() => handleChecklist(single)}
                                    title='Checklist'
                                    style={{
                                      fontSize: '4px',
                                      padding: '4px 4px',
                                      minWidth: '10px',
                                      height: '18px',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    <i
                                      className='fa fa-check-square'
                                      style={{ fontSize: '8px' }}
                                    ></i>
                                  </button>
                                  <button
                                    className='btn btn-outline-info btn-sm'
                                    onClick={() => {
                                      setModal(true);
                                      setModalData(single);
                                      setActivity(single._id);
                                    }}
                                    title='View Details'
                                    style={{
                                      fontSize: '4px',
                                      padding: '4px 4px',
                                      minWidth: '10px',
                                      height: '18px',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    <i
                                      className='fa fa-binoculars'
                                      style={{ fontSize: '8px' }}
                                    ></i>
                                  </button>
                                  <button
                                    className='btn btn-outline-danger btn-sm'
                                    onClick={() =>
                                      handleDeleteClick(single._id)
                                    }
                                    title='Delete'
                                    style={{
                                      fontSize: '4px',
                                      padding: '4px 4px',
                                      minWidth: '10px',
                                      height: '18px',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    <i
                                      className='fa fa-trash'
                                      style={{ fontSize: '8px' }}
                                    ></i>
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={getVisibleColumnsCount()}
                          className='text-center py-5'
                        >
                          <div className='d-flex flex-column align-items-center'>
                            <div
                              className='spinner-border text-primary mb-3'
                              role='status'
                            >
                              <span className='visually-hidden'>
                                Loading...
                              </span>
                            </div>
                            <div
                              className='text-muted'
                              style={{ fontSize: '13px' }}
                            >
                              Loading activities...
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <SmartPagination
                page={page}
                pages={pages}
                total_count={total_activitys}
                setPageNumber={setPageNumber}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditData(null);
        }}
        editData={editData}
        dropdownOptions={dropdownOptions}
        onSave={handleSaveEdit}
        loading={loadingActivityStatus}
      />

      <ColumnSelectorModal
        show={showColumnSelector}
        onHide={() => setShowColumnSelector(false)}
        availableColumns={AVAILABLE_COLUMNS}
        visibleColumns={visibleColumns}
        onSave={handleSaveColumns}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default EnhancedTableSection;
